import React, { useCallback, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  checkNull,
  downloadFileFromUrl,
  getFileNameFromUrl,
  isChildAccount,
  isFutureFromToday,
  isPastFromToday,
  parseStringToDate,
} from "../../../helpers";
import {
  changeTaxEndApi,
  checkTaxYear,
  clearCompanySettingData,
  companySettingData,
  getTaxYearEndDate,
  saveAccountingPeriod,
  uploadAccDocument,
  deleteAccDocument,
  showNotificationWithTimeout,
  updatePostingPeriodStatus,
  scheduleCompanyName,
  getCompanyName,
  toggleCompanyNameModal,
  toggleSettingDocModal,
  toggleSettingAccModal,
} from "../../../Redux/actions";
import ErrorContainer from "../../Components/ErrorContainer";
import Headerbar from "../../Components/InnerLayout/Headerbar";
import UserInterface from "../../Components/InnerLayout/UserInterface";
import moment from "moment";
import ModelContainer from "../../Components/ModelContainer";
import ConfirmModel from "../../Components/ConfirmModel";
import LoadingIcon from "../../Components/LoadingIcon";

const schema = yup
  .object({
    is_first_period: yup.number().required(),
    ap_id: yup.string(),
    tax_year: yup.string().required(),
    account_period_setting_id: yup.string().required(),
    sub_period: yup.string().required(),
    day_of_week: yup.string().when("account_period_setting_id", {
      is: (period_setting) => period_setting != 4,
      then: yup.string().required(),
    }),
    start_date: yup.string().required(),
    end_date: yup.string().required(),
  })
  .required();

const companyNameSchema = yup
  .object({
    company_name: yup.string().required(),
    effective_from: yup.string().required(),
  })
  .required();

const AccountManagement = (params) => {
  const initialAccForm = {
    is_first_period: 1,
    ap_id: "",
    tax_year: "",
    account_period_setting_id: "",
    sub_period: "",
    day_of_week: "",
    start_date: "",
    end_date: "",
  };
  const {
    register,
    formState: { errors: accErrors },
    handleSubmit: accountingSubmit,
    control,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialAccForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const initialCompanyNameForm = {
    company_name: "",
    effective_from: "",
  };
  const {
    register: companyNameReg,
    formState: { errors: companyNameErrors },
    handleSubmit: companyNameSubmit,
    control: companyNameControl,
    setValue: companyNameSetValue,
    reset: companyNameReset,
  } = useForm({
    resolver: yupResolver(companyNameSchema),
    defaultValues: initialCompanyNameForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user_details = useSelector((state) => state.authReducer.user_details);
  const {
    company_setting,
    is_loading,
    is_modal_loading,
    companyNameModal,
    documentModal,
    accountingModal,
  } = useSelector((state) => state.settingReducer);
  const {
    tax_year_res,
    tax_end_date_res,
    tax_end_date_start_from,
    run_tax_end_date_api: taxEndApi,
  } = useSelector((state) => state.authReducer);

  const [accInputForm, setAccInputForm] = useState(initialAccForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [accPeriodId, setAccPeriodId] = useState(null);
  const accPeriodIdRef = useRef(accPeriodId);
  accPeriodIdRef.current = accPeriodId;

  const [accDocumentId, setAccDocumentId] = useState(null);
  const accDocumentIdRef = useRef(accDocumentId);
  accDocumentIdRef.current = accDocumentId;
  const [deleteDocumentModal, setDeleteDocumentModal] = useState(false);

  const [accPostingPeriodId, setAccPostingPeriodId] = useState(null);
  const [accPeriodStatusId, setAccPeriodStatusId] = useState(null);
  const [accPostingPeriodModal, setAccPostingPeriodModal] = useState(false);

  const [companyNameInputForm, setCompanyNameInputForm] = useState(
    initialCompanyNameForm
  );

  const changeCompanyNameInputHandler = (input, val) => {
    companyNameSetValue(input, val);
    setCompanyNameInputForm((prevState) => ({ ...prevState, [input]: val }));
  };

  const changeAccInputHandler = (input, val) => {
    setValue(input, val);
    setAccInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (
      (input == "start_date" && val !== "") ||
      (input != "start_date" &&
        accInputForm?.start_date !== "" &&
        (input == "tax_year" || input == "sub_period"))
    ) {
      onChangeOfTaxYear();
    }
  };

  useEffect(() => {
    if (
      company_setting?.company_name_details &&
      company_setting?.company_name_details !== null
    ) {
      changeCompanyNameInputHandler(
        "company_name",
        company_setting?.company_name_details?.company_name
      );
      changeCompanyNameInputHandler(
        "effective_from",
        moment(company_setting?.company_name_details?.effective_from).toDate()
      );
    } else {
      initialCompanyNameModalState();
    }
  }, [company_setting?.company_name_details]);

  const onFileChangeEvent = (event) => {
    if (event?.target?.files[0]) {
      setSelectedFile(event.target.files[0]);
      event.target.value = null;
    }
  };

  const submitDocument = () => {
    console.log("selectedFile", selectedFile);
    if (selectedFile !== null) {
      const formData = new FormData();
      formData.append("document_url", selectedFile, selectedFile?.name);
      dispatch(uploadAccDocument(formData, accPeriodIdRef.current, id));
      // closeDocumentModal(false);
    }
  };

  const submitAccountingForm = (data) => {
    data.start_date = moment(data?.start_date).format("yyyy-MM-DD");
    data.end_date = moment(data?.end_date).format("yyyy-MM-DD");
    data.add_to_child = 1;
    console.log("submitAccountingForm", data);
    dispatch(saveAccountingPeriod(data, id, "companySettings"));
    // closeModal(false);
  };

  const submitCompanyNameForm = (data) => {
    data.effective_from = moment(data?.effective_from).format("yyyy-MM-DD");
    console.log("submitCompanyNameForm", data);
    dispatch(scheduleCompanyName(data, id));
    // closeCompanyNameModal(false);
  };

  const onChangeOfTaxYear = () => {
    if (accInputForm?.is_first_period == 0) {
      dispatch(
        checkTaxYear({
          tax_year: accInputForm?.tax_year,
        })
      );
    } else {
      dispatch(
        changeTaxEndApi({
          run_tax_end_date_api: taxEndApi == null ? true : !taxEndApi,
        })
      );
    }
  };
  useEffect(() => {
    if (taxEndApi !== null)
      dispatch(
        getTaxYearEndDate({
          ...accInputForm,
          start_date:
            accInputForm?.start_date && accInputForm?.start_date !== ""
              ? moment(accInputForm?.start_date).format("yyyy-MM-DD")
              : "",
          end_date:
            accInputForm?.end_date && accInputForm?.end_date !== ""
              ? moment(accInputForm?.end_date).format("yyyy-MM-DD")
              : "",
        })
      );
  }, [taxEndApi]);
  const initialModalState = () => {
    setAccInputForm(initialAccForm);
    reset(initialAccForm);
    changeTaxEndApi({
      run_tax_end_date_api: null,
      tax_end_date_res: null,
    });
  };
  const initialCompanyNameModalState = () => {
    setCompanyNameInputForm(initialCompanyNameForm);
    companyNameReset(initialCompanyNameForm);
  };

  const closeModal = useCallback(
    (value) => {
      dispatch(toggleSettingAccModal(value));
      initialModalState();
    },
    [accountingModal]
  );
  const closeDocumentModal = useCallback(
    (value) => {
      dispatch(toggleSettingDocModal(value));
      setSelectedFile(null);
    },
    [documentModal]
  );
  const closeDeleteDocumentModal = useCallback(
    (value) => {
      setDeleteDocumentModal(value);
      if (!value) setAccPeriodId(null);
    },
    [documentModal]
  );
  const closeCompanyNameModal = useCallback(
    (value) => {
      if (value) {
        dispatch(getCompanyName(id));
      } else {
        dispatch(toggleCompanyNameModal(false));
      }
      initialCompanyNameModalState();
    },
    [companyNameModal]
  );
  const submitDeleteDocumentHandler = () => {
    dispatch(deleteAccDocument({ id: accDocumentIdRef.current }, id));
    closeDeleteDocumentModal(false);
  };

  const closeAccPostingPeriodModal = useCallback(
    (value) => {
      setAccPostingPeriodModal(value);
      if (!value) {
        setAccPostingPeriodId(null);
        setAccPeriodStatusId(null);
      }
    },
    [accPostingPeriodModal]
  );
  const submitAccPostingPeriodHandler = () => {
    dispatch(
      updatePostingPeriodStatus(
        { id: accPostingPeriodId, status_id: accPeriodStatusId },
        id
      )
    );
    closeAccPostingPeriodModal(false);
  };

  useEffect(() => {
    if (tax_end_date_res?.return == 1 && tax_end_date_res?.data) {
      if (tax_end_date_start_from) {
        changeAccInputHandler(
          "end_date",
          moment(tax_end_date_res?.data).toDate()
        );
      } else {
        changeAccInputHandler(
          "start_date",
          moment(tax_end_date_res?.data).toDate()
        );
      }
    } else if (tax_end_date_res?.return == 0) {
      changeAccInputHandler("end_date", "");
      changeAccInputHandler("start_date", "");
      if (tax_end_date_res?.message) {
        dispatch(
          showNotificationWithTimeout(`${tax_end_date_res?.message}`, "error")
        );
      }
    }
  }, [tax_end_date_res]);

  useEffect(() => {
    dispatch(companySettingData(id));
    dispatch(
      changeTaxEndApi({
        run_tax_end_date_api: null,
        tax_end_date_res: null,
      })
    );
    return () => {
      dispatch(clearCompanySettingData());
    };
  }, []);

  const editAccountingHandler = (period) => {
    closeModal(true);
    const editValues = {
      is_first_period: accInputForm?.is_first_period,
      ap_id: period?.id ?? "",
      tax_year: period?.tax_year ?? "",
      account_period_setting_id: period?.account_period_setting_id ?? "",
      sub_period: period?.sub_period ?? "",
      day_of_week: period?.day_of_week ?? "",
      start_date: period?.start_date ? moment(period?.start_date).toDate() : "",
      end_date: period?.end_date ? moment(period?.end_date).toDate() : "",
    };
    setAccInputForm(editValues);
    reset(editValues);
  };

  const getAccPeriodStatusBtn = (period) => {
    let res = null;
    if (
      checkNull(period?.period_end, false) &&
      checkNull(period?.period_start, false) &&
      isPastFromToday(period?.period_start, true) &&
      isFutureFromToday(period?.period_end, true)
    ) {
      res = (
        <button class="btn cancel" type="button">
          In Progress
        </button>
      );
    } else if (period?.status_id == 56) {
      res = (
        <button
          className={`btn bordered-btn mr0${
            isChildAccount(user_details) ? "" : " update_status"
          }`}
          val="59"
          val_id={period?.id}
          type="button"
          onClick={() => {
            setAccPostingPeriodId(period?.id);
            setAccPeriodStatusId("59");
            closeAccPostingPeriodModal(true);
          }}
        >
          Close Period
        </button>
      );
    } else if (period?.status_id == 58) {
      res = (
        <button
          className={`btn bordered cancel${
            isChildAccount(user_details) ? "" : " update_status"
          }`}
          val="56"
          val_id={period?.id}
          type="button"
          onClick={() => {
            setAccPostingPeriodId(period?.id);
            setAccPeriodStatusId("56");
            closeAccPostingPeriodModal(true);
          }}
        >
          Open Period
        </button>
      );
    } else if (period?.status_id == 59) {
      res = (
        <button
          className={`btn bordered-btn cancel${
            isChildAccount(user_details) ? "" : " update_status"
          }`}
          val="56"
          val_id={period?.id}
          type="button"
          onClick={() => {
            setAccPostingPeriodId(period?.id);
            setAccPeriodStatusId("56");
            closeAccPostingPeriodModal(true);
          }}
        >
          Reopen Period
        </button>
      );
    }
    return res;
  };

  return (
    <UserInterface>
      <div className="no-sidebar row" id="content">
        <Headerbar type={"company-settings"} />

        <div className="inner-content row mx-0">
          <div className="container px-0">
            <div className="top-header-sec py-3 mb-0">
              <h3 className="event-alert br-btm-0"> Company Settings </h3>
              <p className="mb-0">
                The below are the settings specific to the given legal entity,
                including certain tax and accounting related information.
              </p>
            </div>

            <div className="shadowclass">
              <div className="row-fluid">
                <div className="row">
                  <div className="col-left col-8">
                    <h4 className="event-alert br-btm-0 m0 p0 mb-2 d-flex align-center">
                      {company_setting?.merchantInfo?.merchant_balance_sheet
                        ?.balance_sheet_name ?? ""}
                      <a
                        onClick={() => closeCompanyNameModal(true)}
                        className="circle_icon schedule_company_name_btn mx-3"
                      >
                        <LoadingIcon
                          is_loading={is_loading}
                          className="iconSprites edit-icon"
                        />
                      </a>
                    </h4>

                    <div className="taxText">
                      <p>
                        {" "}
                        Tax ID :{" "}
                        {company_setting?.merchantInfo?.company_tax_id ??
                          ""}{" "}
                      </p>
                      <p>
                        {" "}
                        Currency :{" "}
                        {company_setting?.merchantInfo?.currency
                          ?.currency_name ?? ""}{" "}
                      </p>
                    </div>
                  </div>

                  <div className="col-right col-4">
                    <span className="green_tag"> Active </span> <br />
                    <i className="text-right w-100 md-font">
                      {" "}
                      Incorporated on{" "}
                      {checkNull(
                        company_setting?.merchantInfo?.incorporation_date,
                        false
                      )
                        ? parseStringToDate(
                            company_setting?.merchantInfo?.incorporation_date,
                            undefined,
                            "DD-MM-yyyy"
                          )
                        : ""}
                    </i>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mx-0 col-12">
              <div className="container px-0">
                <div className="row pt-4 pb-1">
                  <div className="col-6 d-flex align-items-center">
                    <h4 className="event-alert p-0 mb-0">
                      {" "}
                      Accounting Tax Years{" "}
                    </h4>
                  </div>
                  <div className="col-6 align-right">
                    {!isChildAccount(user_details) && (
                      <button
                        className="btn green-btn-revert add-plus addAp mx-0"
                        type="button"
                        onClick={() => closeModal(true)}
                      >
                        <i className="i-icon"> </i> Add Tax Year{" "}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex">
              <div className="shadowclass">
                <div className="table-responsive">
                  <table className="checklist-table remove-space inventory_table">
                    <thead>
                      <tr>
                        <th width="160">
                          <a href="#_" className="">
                            {" "}
                            Tax Year{" "}
                          </a>
                        </th>
                        <th>
                          Accounting <br /> Period Setting
                        </th>
                        <th>Sub-Periods</th>
                        <th>Period Start</th>
                        <th>Period End</th>
                        <th>Documents</th>
                        <th width="12%" className="hide-small"></th>
                        <th className="action-td">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {company_setting?.accountingPeriods &&
                        company_setting?.accountingPeriods.length > 0 &&
                        company_setting?.accountingPeriods.map(
                          (accountingPeriod) => {
                            return (
                              <tr>
                                <td>{accountingPeriod?.tax_year ?? ""}</td>
                                <td>
                                  {accountingPeriod?.account_period_setting
                                    ?.name ?? ""}
                                </td>
                                <td>
                                  {accountingPeriod?.sub_period
                                    ? capitalizeFirstLetter(
                                        accountingPeriod?.sub_period
                                      )
                                    : ""}
                                </td>
                                <td>
                                  {checkNull(
                                    accountingPeriod?.start_date,
                                    false
                                  )
                                    ? parseStringToDate(
                                        accountingPeriod?.start_date,
                                        "yyyy-MM-DD",
                                        "MMMM DD, yyyy"
                                      )
                                    : ""}
                                </td>
                                <td>
                                  {checkNull(accountingPeriod?.end_date, false)
                                    ? parseStringToDate(
                                        accountingPeriod?.end_date,
                                        "yyyy-MM-DD",
                                        "MMMM DD, yyyy"
                                      )
                                    : ""}
                                </td>
                                <td class="attachmentTd">
                                  <div class="attach-row">
                                    {accountingPeriod?.accounting_period_document &&
                                      accountingPeriod
                                        ?.accounting_period_document.length >
                                        0 &&
                                      accountingPeriod?.accounting_period_document.map(
                                        (document) => {
                                          return (
                                            <>
                                              <div class="attachments">
                                                <a
                                                  role="button"
                                                  onClick={() =>
                                                    downloadFileFromUrl(
                                                      document?.document_url,
                                                      getFileNameFromUrl(
                                                        document?.document_url
                                                      )
                                                    )
                                                  }
                                                >
                                                  <i class="icon"> </i>{" "}
                                                  {checkNull(
                                                    document?.document_url,
                                                    false
                                                  )
                                                    ? getFileNameFromUrl(
                                                        document?.document_url
                                                      )
                                                    : ""}
                                                </a>
                                              </div>
                                              {!isChildAccount(
                                                user_details
                                              ) && (
                                                <div
                                                  class="circle_icon red"
                                                  onClick={() => {
                                                    setAccDocumentId(
                                                      document?.id ?? null
                                                    );
                                                    closeDeleteDocumentModal(
                                                      true
                                                    );
                                                  }}
                                                >
                                                  <i
                                                    class="iconSprites delete-icon delAccDoc"
                                                    val="8"
                                                  ></i>
                                                </div>
                                              )}
                                            </>
                                          );
                                        }
                                      )}
                                  </div>
                                </td>
                                <td>
                                  <a
                                    className="btn bordered-btn add-plus addDocAccounting"
                                    count="325"
                                    onClick={() => {
                                      setAccPeriodId(
                                        accountingPeriod?.id ?? null
                                      );
                                      closeDocumentModal(true);
                                    }}
                                  >
                                    <i className="i-icon"></i> Add Document
                                  </a>
                                </td>
                                <td width="15px" className="action-td">
                                  {checkNull(
                                    accountingPeriod?.start_date,
                                    false
                                  ) &&
                                    isPastFromToday(
                                      accountingPeriod?.start_date
                                    ) &&
                                    !isChildAccount(user_details) && (
                                      <a
                                        role="button"
                                        class="circle_icon editApFinance"
                                        onClick={() =>
                                          editAccountingHandler(
                                            accountingPeriod
                                          )
                                        }
                                      >
                                        <i class="iconSprites edit-icon">
                                          <div class="tooltip-sm"> Edit </div>
                                        </i>
                                      </a>
                                    )}
                                </td>
                              </tr>
                            );
                          }
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="inner-content row mx-0">
          <div className="container px-0">
            <div className="row pt-4 pb-1">
              <div className="col-6 d-flex align-items-center">
                <h4 className="event-alert p-0 mb-0"> Posting Periods</h4>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex">
              <div className="shadowclass">
                <div className="table-responsive">
                  <table className="checklist-table remove-space inventory_table">
                    <thead>
                      <tr>
                        <th width="160">Status</th>
                        <th width="135">Tax Year</th>
                        <th>Period Start</th>
                        <th>Period End</th>
                        <th>Date Closed</th>
                        <th>Closed By</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {company_setting?.posting_period &&
                        company_setting?.posting_period.length > 0 &&
                        company_setting?.posting_period.map((period) => {
                          return (
                            <tr>
                              <td>
                                <span
                                  className={
                                    period?.status_id == 56
                                      ? "green_tag"
                                      : "grey_tag"
                                  }
                                >
                                  {company_setting?.posting_period_status?.[
                                    period?.status_id
                                  ] ?? ""}
                                </span>
                              </td>
                              <td class="">{period?.tax_year ?? ""}</td>
                              <td>
                                {checkNull(period?.period_start, false)
                                  ? parseStringToDate(
                                      period?.period_start,
                                      "yyyy-MM-DD",
                                      "MMMM DD, yyyy"
                                    )
                                  : ""}
                              </td>
                              <td>
                                {checkNull(period?.period_start, false)
                                  ? parseStringToDate(
                                      period?.period_end,
                                      "yyyy-MM-DD",
                                      "MMMM DD, yyyy"
                                    )
                                  : ""}
                              </td>
                              <td>
                                {checkNull(period?.period_start, false)
                                  ? parseStringToDate(
                                      period?.date_closed,
                                      "yyyy-MM-DD",
                                      "MMMM DD, yyyy"
                                    )
                                  : ""}
                              </td>
                              <td>
                                {checkNull(period?.closed_by, false)
                                  ? `${period?.user?.first_name} ${period?.user?.last_name}`
                                  : ""}
                              </td>
                              <td className="align-right">
                                {getAccPeriodStatusBtn(period)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModelContainer
        title={`${
          accInputForm?.ap_id !== "" ? "Edit" : "Add"
        } Accounting Period`}
        open={accountingModal}
        closeModal={closeModal}
        submitModal={accountingSubmit(submitAccountingForm)}
        is_loading={is_modal_loading}
      >
        <form>
          <input
            type="hidden"
            name="is_first_period"
            {...register("is_first_period")}
            value={accInputForm?.is_first_period}
          />
          <input
            type="hidden"
            name="ap_id"
            {...register("ap_id")}
            value={accInputForm?.ap_id}
          />
          {console.log("accErrors", accErrors)}
          <div className="form-list">
            <ul className="px-0">
              <li>
                <label className="required"> Tax Year </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("tax_year")}
                    name="tax_year"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler("tax_year", event?.target?.value)
                    }
                    value={accInputForm?.tax_year}
                  >
                    <option value=""> Select Tax Year </option>
                    {company_setting?.tenYearsBetween &&
                      company_setting?.tenYearsBetween !== null &&
                      accInputForm?.ap_id == "" &&
                      Object.keys(company_setting?.tenYearsBetween).map(
                        (index) => {
                          return (
                            <option value={index} key={index}>
                              {" "}
                              {company_setting?.tenYearsBetween?.[index]}{" "}
                            </option>
                          );
                        }
                      )}
                    {accInputForm?.ap_id !== "" && (
                      <option value={accInputForm?.tax_year}>
                        {accInputForm?.tax_year}
                      </option>
                    )}
                  </select>
                  <ErrorContainer errors={accErrors} name="tax_year" />
                </div>
              </li>

              <li>
                <label className="required"> Account Period Setting </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("account_period_setting_id")}
                    name="account_period_setting_id"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler(
                        "account_period_setting_id",
                        event?.target?.value
                      )
                    }
                    value={accInputForm?.account_period_setting_id}
                  >
                    <option value=""> Select </option>
                    {company_setting?.accountPeriodSettings &&
                      company_setting?.accountPeriodSettings !== null &&
                      company_setting?.accountPeriodSettings.map((psetting) => {
                        return (
                          <option value={psetting?.id} key={psetting?.id}>
                            {" "}
                            {psetting?.name}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer
                    errors={accErrors}
                    name="account_period_setting_id"
                  />
                </div>
              </li>

              <li>
                <label className="required"> Sub Period </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("sub_period")}
                    name="sub_period"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler("sub_period", event?.target?.value)
                    }
                    value={accInputForm?.sub_period}
                  >
                    <option value=""> Select </option>
                    {company_setting?.sub_period &&
                      company_setting?.sub_period !== null > 0 &&
                      Object.keys(company_setting?.sub_period).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {company_setting?.sub_period?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={accErrors} name="sub_period" />
                </div>
              </li>

              {accInputForm?.account_period_setting_id &&
                accInputForm?.account_period_setting_id !== "" &&
                [1, 2, 3].indexOf(
                  parseInt(accInputForm?.account_period_setting_id)
                ) != -1 && (
                  <li class="settingsLi">
                    <label for="AccountingPeriodDayOfWeek" class="required">
                      Setting Type
                    </label>
                    <div class="input-wrap select-div">
                      <select
                        {...register("day_of_week")}
                        name="day_of_week"
                        class="required"
                        id="AccountingPeriodDayOfWeek"
                        onChange={(event) =>
                          changeAccInputHandler(
                            "day_of_week",
                            event?.target?.value
                          )
                        }
                        value={accInputForm?.day_of_week}
                      >
                        <option value="">Select Setting Type</option>
                        {company_setting?.settingTypes &&
                          company_setting?.settingTypes !== null &&
                          Object.keys(company_setting?.settingTypes).map(
                            (index) => {
                              return (
                                <option value={index} key={index}>
                                  {" "}
                                  {company_setting?.settingTypes?.[index]}{" "}
                                </option>
                              );
                            }
                          )}
                      </select>
                      <ErrorContainer errors={accErrors} name="day_of_week" />
                    </div>
                  </li>
                )}

              <li>
                <label className="required"> Period Start </label>
                <div className="input-wrap">
                  {/* <input
                    type="text"
                    name="start_date"
                    className="datepicker"
                  ></input> */}
                  <DatePicker
                    {...register("start_date")}
                    name="start_date"
                    autoComplete="new-password"
                    dateFormat="yyyy-MM-dd"
                    onChange={(val) => changeAccInputHandler("start_date", val)}
                    selected={accInputForm?.start_date}
                  />
                  {/* <span class="add-on">
                    <i class="fa fa-calendar"></i>
                  </span> */}
                </div>
                <ErrorContainer errors={accErrors} name="start_date" />
              </li>

              <li>
                <label className="required"> Period End</label>
                <div className="input-wrap">
                  {/* <input
                    type="text"
                    name="end_date"
                    className="datepicker"
                  ></input> */}
                  <DatePicker
                    {...register("end_date")}
                    name="end_date"
                    autoComplete="new-password"
                    dateFormat="yyyy-MM-dd"
                    onChange={(val) => changeAccInputHandler("end_date", val)}
                    selected={accInputForm?.end_date}
                    readonly
                    disabled
                  />
                  {/* <span class="add-on">
                    <i class="fa fa-calendar"></i>
                  </span> */}
                </div>
                <ErrorContainer errors={accErrors} name="end_date" />
              </li>
            </ul>
          </div>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`Add Document`}
        open={documentModal}
        closeModal={closeDocumentModal}
        submitModal={submitDocument}
        is_loading={is_modal_loading}
      >
        <fieldset class="">
          <ul class="form-list label-group new-form d-flex">
            {selectedFile !== null && (
              <li className="text-center mx-4 my-3">
                <img
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                  src={URL.createObjectURL(selectedFile)}
                />
              </li>
            )}
            <li>
              <label for="AccountingPeriodDocumentDocumentUrl" class="required">
                Upload Document
              </label>{" "}
              <div class="input-wrap browse-sec">
                <input
                  type="file"
                  class="required"
                  id="AccountingPeriodDocumentDocumentUrl"
                  onChange={onFileChangeEvent}
                />
              </div>
            </li>
          </ul>
        </fieldset>
      </ModelContainer>
      <ModelContainer
        title={`Change Company Name`}
        open={companyNameModal}
        closeModal={closeCompanyNameModal}
        submitModal={companyNameSubmit(submitCompanyNameForm)}
        is_loading={is_modal_loading}
      >
        <form>
          {console.log("companyNameErrors", companyNameErrors)}
          <div className="form-list">
            <ul className="px-0">
              <li>
                <label className="required"> Company Name</label>
                <div className="input-group mb10">
                  <input
                    {...register("company_name")}
                    className="required"
                    type="text"
                    onChange={(event) =>
                      changeCompanyNameInputHandler(
                        "company_name",
                        event?.target?.value
                      )
                    }
                    value={companyNameInputForm?.company_name}
                  />
                  <ErrorContainer
                    errors={companyNameErrors}
                    name="company_name"
                  />
                </div>
              </li>
              <li>
                <label className="required"> Effective From</label>
                <div className="input-wrap">
                  <DatePicker
                    {...companyNameReg("effective_from")}
                    name="effective_from"
                    autoComplete="new-password"
                    dateFormat="yyyy-MM-dd"
                    onChange={(val) =>
                      changeCompanyNameInputHandler("effective_from", val)
                    }
                    selected={companyNameInputForm?.effective_from}
                  />
                  {/* <span class="add-on">
                    <i class="fa fa-calendar"></i>
                  </span> */}
                </div>
                <ErrorContainer
                  errors={companyNameErrors}
                  name="effective_from"
                />
              </li>
            </ul>
          </div>
        </form>
      </ModelContainer>
      <ConfirmModel
        title="Delete Document"
        text="are you sure?"
        open={deleteDocumentModal}
        closeModal={closeDeleteDocumentModal}
        submitModal={submitDeleteDocumentHandler}
        is_loading={false}
      />
      <ConfirmModel
        title="Update Status"
        text="Are you sure you want to update the status?"
        open={accPostingPeriodModal}
        closeModal={closeAccPostingPeriodModal}
        submitModal={submitAccPostingPeriodHandler}
        is_loading={false}
      />
    </UserInterface>
  );
};
export default AccountManagement;
